/**
 * Utility functions for extracting thumbnails from video files
 * Uses HTML5 Canvas and Video APIs for client-side extraction
 */

/**
 * Extracts a single frame from a video file at a specified time
 * @param {File} videoFile - The video file to extract from
 * @param {number} timeInSeconds - The timestamp to extract (default: 1)
 * @param {Object} options - Optional configuration
 * @param {number} options.maxWidth - Maximum thumbnail width (default: 640)
 * @param {number} options.maxHeight - Maximum thumbnail height (default: 360)
 * @param {string} options.format - Output format: 'image/jpeg' or 'image/png'
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.85)
 * @returns {Promise<{blob: Blob, dataUrl: string, width: number, height: number}>}
 */
interface ExtractVideoFrameOptions {
    maxWidth?: number;
    maxHeight?: number;
    format?: string;
    quality?: number;
}

interface ExtractedVideoFrame {
    blob: Blob;
    dataUrl: string;
    width: number;
    height: number;
}

export const extractVideoFrame = (
    videoFile: File,
    timeInSeconds = 1,
    options: ExtractVideoFrameOptions = {}
): Promise<ExtractedVideoFrame> => {
    const {
        maxWidth = 640,
        maxHeight = 360,
        format = 'image/jpeg',
        quality = 0.85
    } = options;

    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;

        const objectUrl = URL.createObjectURL(videoFile);

        const cleanup = () => {
            URL.revokeObjectURL(objectUrl);
            video.src = '';
            video.load();
        };

        video.onloadedmetadata = () => {
            // Clamp time to video duration
            const seekTime = Math.min(timeInSeconds, video.duration);
            video.currentTime = seekTime;
        };

        video.onseeked = () => {
            // Calculate dimensions maintaining aspect ratio
            let width = video.videoWidth;
            let height = video.videoHeight;

            if (width > maxWidth) {
                height = (maxWidth / width) * height;
                width = maxWidth;
            }
            if (height > maxHeight) {
                width = (maxHeight / height) * width;
                height = maxHeight;
            }

            canvas.width = width;
            canvas.height = height;
            if (!ctx) {
                cleanup();
                reject(new Error('Failed to get 2d canvas context'));
                return;
            }
            ctx.drawImage(video, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        const dataUrl = canvas.toDataURL(format, quality);
                        cleanup();
                        resolve({
                            blob,
                            dataUrl,
                            width,
                            height
                        });
                    } else {
                        cleanup();
                        reject(new Error('Failed to create blob from canvas'));
                    }
                },
                format,
                quality
            );
        };

        video.onerror = () => {
            cleanup();
            reject(new Error('Failed to load video file'));
        };

        video.src = objectUrl;
    });
};

/**
 * Gets video metadata (duration, dimensions) from a file
 * @param {File} videoFile - The video file
 * @returns {Promise<{duration: number, width: number, height: number}>}
 */
interface VideoMetadata {
    duration: number;
    width: number;
    height: number;
}

export const getVideoMetadata = (videoFile: File): Promise<VideoMetadata> => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';

        const objectUrl = URL.createObjectURL(videoFile);

        video.onloadedmetadata = () => {
            const metadata = {
                duration: video.duration,
                width: video.videoWidth,
                height: video.videoHeight
            };
            URL.revokeObjectURL(objectUrl);
            resolve(metadata);
        };

        video.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Failed to load video metadata'));
        };

        video.src = objectUrl;
    });
};

/**
 * Generates a thumbnail filename based on the video filename
 * @param {string} videoFilename - Original video filename
 * @returns {string} Thumbnail filename
 */
export const generateThumbnailFilename = (videoFilename: string): string => {
    const baseName = videoFilename.replace(/\.[^.]+$/, '');
    return `${baseName}_thumbnail.jpg`;
};
